import {
    Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TableSortLabel, Tooltip, Typography, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { iconSize } from 'assets/theme/iconSize';
import { setLoading, useMaterialUIController } from 'context';
import { ITableProps } from '@types_main/itable.props';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { QuickReplyResponse } from '../services/responses/quick-reply.response';
import { QuickReplyService } from '../services/quick-reply.service';

export function QuickReplyTable(props: ITableProps<QuickReplyResponse>) {
    const { t } = useTranslation();
    const { orderDirection, handleOrderList, data, editAction } = props;
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();

    function doDelete(id: string) {
        setLoading(dispatch, true);
        new QuickReplyService()
            .deleteById(id)
            .then(() => {
                showAlert(t('quickReplies.excluirSucesso'), 'success', 2500);
                props.setRefresh(!props.refresh);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 3000))
            .finally(() => setLoading(dispatch, false));
    }

    return (
        <Grid>
            <TableContainer sx={{ marginTop: '15px', maxHeight: '60vh', overflowY: 'auto' }}>
                <Table size="medium">
                    <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                        <TableRow hover>
                            <TableCell width="22%">
                                {t('quickReplies.atalho')}
                                <TableSortLabel
                                    active={true}
                                    direction={orderDirection}
                                    onClick={handleOrderList}
                                ></TableSortLabel>
                            </TableCell>
                            <TableCell width="60%">{t('quickReplies.conteudo')}</TableCell>
                            <TableCell width="18%" align="center">
                                {t('general.acoes')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.data ? (
                            data.data.records.length > 0 ? (
                                data.data.records.map((item) => (
                                    <TableRow hover key={item.id}>
                                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                                            {item.shortcut}
                                        </TableCell>
                                        <TableCell>
                                            {/* Duas linhas bastam para reconhecer a resposta; o texto inteiro fica na edição. */}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    overflowWrap: 'anywhere',
                                                }}
                                            >
                                                {item.content}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <Tooltip title={t('general.editar')}>
                                                    <IconButton size="small" sx={{ p: 0.25 }} onClick={() => editAction(item.id)}>
                                                        <EditIcon
                                                            style={iconSize}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('general.excluir')}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ p: 0.25 }}
                                                        onClick={() =>
                                                            alertDelete(
                                                                item.id,
                                                                doDelete,
                                                                item.shortcut
                                                            )
                                                        }
                                                    >
                                                        <DeleteIcon
                                                            sx={iconSize}
                                                            color="error"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow hover>
                                    <TableCell colSpan={3} align="center">
                                        {t('general.nenhumRegistro')}
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            Array.from({ length: 5 }, (_, i) => i).map((index) => (
                                <TableRow hover key={index}>
                                    <TableCell><Skeleton height={30} variant="rectangular" /></TableCell>
                                    <TableCell><Skeleton height={30} variant="rectangular" /></TableCell>
                                    <TableCell><Skeleton height={30} variant="rectangular" /></TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
}
